// @flow

import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { Page } from "react-page-layout";
import classNames from "classnames";
import moment from "moment";
import config from "@config";
import withErrorHandler from "@components/error";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components/";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import { Loader, TitleSection, View, GSGrid, Button, ChartJS, Text } from "@components";
import serviceActions from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";

import type { State } from "@store/reducers";

type PropsFromState = $Exact<{
  appAdoptionHighlights: Object,
  appAdoptionDepartments: Object,
}>;

type PropsFromDispatch = $Exact<{
  getAppAdoptionHighlights: typeof serviceActions.GetAppAdoptionHighlights,
  getAppAdoptionDepartments: typeof serviceActions.GetAppAdoptionDepartments,
  exportAppAdoptionDepartments: typeof serviceActions.ExportAppAdoptionDepartments,
}>;

type Props = $Exact<{
  ...PropsFromPermissions,
  ...PropsFromClientContext,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;
export type SortingOptionsType = "lowest" | "highest";
type AlertsState = $Exact<{
  resultsSort: SortingOptionsType,
}>;

export function AdoptionTab(props: Props) {
  const [resultsSort, setResultsSort] = useState("lowest");

  const resultsDepartments = props.appAdoptionDepartments.data || [];
  const columns = [
    {
      name: props.t("RANK"),
      align: "center",
      width: 50,
      key: "rank",
    },
    {
      name: props.t("DEPARTMENT_NAME"),
      key: "department",
      align: "left",
    },
    {
      name: props.t("ACTIVE_EMPLOYEES"),
      align: "center",
      key: "activeEmployees",
    },
    {
      name: props.t("ONBOARDED"),
      align: "center",
      key: "onboarded",
    },
    {
      name: props.t("ADOPTION_RATE"),
      align: "center",
      width: 100,
      key: "adoptionRate",
      renderCell: ({ row }: Object) =>
        GSGrid.render.barIndicator({
          total: row.adoptionRate,
          max: 100,
          color: "rgba(255, 221, 180, 1)",
        }),
    },
  ];

  useEffect(() => {
    props.getAppAdoptionHighlights({ clientId: props.clientId });
    props.getAppAdoptionDepartments({
      clientId: props.clientId,
      sort: resultsSort,
    });
  }, [props.clientId, resultsSort]);

  function setSort(sort: SortingOptionsType) {
    return function() {
      if (sort !== resultsSort) {
        setResultsSort(sort);
        props.getAppAdoptionDepartments({
          clientId: props.clientId,
          sort,
        });
      }
    };
  }
  function exportResults() {
    props.exportAppAdoptionDepartments({
      clientId: props.clientId,
      sort: resultsSort,
    });
  }

  function employeesOnboarded(): Object {
    const onboardedToApp = props.appAdoptionHighlights.onboardedToApp;

    return {
      labels: onboardedToApp ? [props.t("ONBOARDED"), props.t("NOT_ONBOARDED")] : [],
      datasets: [
        {
          label: props.t("PERCENTAGE"),
          data: onboardedToApp ? [onboardedToApp.onboarded, onboardedToApp.notOnboarded] : [],
        },
      ],
    };
  }
  function downloadsInLast14Days(): Object {
    const downloads = props.appAdoptionHighlights.downloads || [];

    return {
      labels: downloads.map((download: Object) => moment(download.date).format(config.dateFormat)),
      datasets: [
        {
          label: props.t("ONBOARDED"),
          data: downloads.map((download: Object) => download.onboarded),
        },
      ],
    };
  }

  return (
    <View gap={6}>
      <View id="highlights" wrapperClassName="flex-column mb-4" className="pt-0">
        {props.appAdoptionHighlights.isLoading && <Loader inline />}

        <View direction="row" gap={6}>
          <View className="gs-container" flex={1}>
            <ChartJS
              type="doughnut"
              title={props.t("PERCENTAGE_OF_EMPLOYEES_ONBOARDED")}
              data={employeesOnboarded()}
            />
          </View>
          <View className="gs-container" flex={1}>
            <ChartJS
              type="bar"
              title={props.t("DOWNLOADS_IN_LAST_DAYS")}
              data={downloadsInLast14Days()}
              options={{ height: 260, labelsOverBars: true }}
            />
          </View>
        </View>
      </View>
      <View className="gs-container">
        <View direction="row" justifyContent="space-between">
          <View gap="3" alignItems="center" direction="row" flex="2">
            <h6>{props.t("RESULTS")}</h6>
            <View alignItems="center" direction="row">
              <Text text={props.t("SORT_BY")} />
              <Button
                disabled={props.appAdoptionDepartments.isLoading}
                onClick={setSort("lowest")}
                className={classNames("ml-2", {
                  selected: resultsSort === "lowest",
                })}
                size="small"
              >
                {props.t("LOWEST")}
              </Button>
              <Button
                disabled={props.appAdoptionDepartments.isLoading}
                onClick={setSort("highest")}
                className={classNames("ml-2", {
                  selected: resultsSort === "highest",
                })}
                label={props.t("HIGHEST")}
                size="small"
              />
            </View>
          </View>
          <View direction="row" justifyContent="end" flex="1">
            <Button
              materialIcon="file_download"
              disabled={props.appAdoptionDepartments.isLoading}
              onClick={exportResults}
              label={props.t("EXPORT")}
              size="small"
            />
          </View>
        </View>
        {props.appAdoptionDepartments.isLoading && <Loader inline />}

        <GSGrid columns={columns} rows={resultsDepartments} />
      </View>
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    appAdoptionHighlights: serviceSelectors.selectAppAdoptionHighlights(state),
    appAdoptionDepartments: serviceSelectors.selectAppAdoptionDepartments(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getAppAdoptionHighlights: serviceActions.GetAppAdoptionHighlights,
  getAppAdoptionDepartments: serviceActions.GetAppAdoptionDepartments,
  exportAppAdoptionDepartments: serviceActions.ExportAppAdoptionDepartments,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AdoptionTab);
